// app/src/features/timeline/useLaneCtx.js
// Moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1; reads props and selectors, never the store.
import { useSyncExternalStore } from "react";
import { ResolutionIcon } from "../../components/Icons.jsx";
import { Tooltip } from "../../components/Overlays.jsx";
import { isLightTheme } from "../../lib/colors.js";
import { getDetailFailed, subscribeDetailFailed } from "../../lib/forecast/detailBackfill.js";
import { forecasterDebugLabel, rootEngine } from "../../lib/forecast/engines.js";
import {
  parseResolveDateLocal,
  resolutionClampIso,
  resolutionMomentLocal,
} from "../../lib/formatDates.js";
import { STREAM_HALF } from "./renderers/constants.js";
import { TODAY } from "./useTimelineGeometry.js";

/**
 * Builds ForecastLane's `ctx` (seam S-11: the eleven groups, field list unchanged) from the
 * timeline geometry and the app state it renders. Not memoized: the render primitives it carries are
 * recreated each render, so a memo would capture stale closures.
 */
export function useLaneCtx(inputs) {
  const {
    CALIBRATION_MODEL_ID,
    LANE_COLORS,
    RES_SZ,
    SPINE_Y,
    _resHasTerminal,
    _resVerdictStatus,
    allMultiwayEnabled,
    animPhase,
    branchAnim,
    calibration,
    clearBranchPremise,
    dayToScreen,
    debugResolutionOverride,
    earlyResolved,
    forecastEngine,
    forecastLiveProgressEnabled,
    forecastTracking,
    glowControls,
    glowSpine,
    hov,
    hovPos,
    hoveredBranch,
    openReport,
    premiseViews,
    redBlueTeam,
    removeBranch,
    renderRow,
    resolvedAtIso,
    resolvedScreenX,
    scrubbedToForecast,
    setHov,
    setHovPos,
    setHoveredBin,
    setHoveredBranch,
    theme,
    themeRaw,
    timelineToday,
    todayX,
    toggleBranchCollapsed,
    uiZoom,
    vw,
  } = inputs;
  const detailFailed = useSyncExternalStore(
    subscribeDetailFailed,
    getDetailFailed,
    getDetailFailed,
  );

  // Assembled once per render and deliberately NOT memoized: the leaf render primitives below
  // (renderRow/renderEvt/computeVisibility/…) are plain consts recreated each render, so a memo
  // would capture stale closures.
  const laneCtx = {
    geom: {
      dayToScreen,
      vw,
      SPINE_Y,
      TODAY,
      effectiveToday: timelineToday,
      todayX,
      // Nothing covers the timeline on the right: the rail's panel opens on the left.
      rightInset: 0,
    },
    anim: { animPhase, branchAnim },
    // ForecastLane + GlowSpine render the spine/pies as SVG presentation attributes, which don't
    // resolve var(), so they consume the raw-hex palette, not the frozen var-string `C`. `isLight`
    // lifts the dotted spline a couple shades in the dark schemes only; the light themes keep their
    // tuned value.
    colors: { C: themeRaw, LANE_COLORS, redBlueTeam, isLight: isLightTheme(theme) },
    render: { renderRow },
    hover: {
      hov,
      hovPos,
      setHov,
      setHovPos,
      hoveredBranch,
      setHoveredBranch,
      setHoveredBin,
      uiZoom,
    },
    branch: { removeBranch, toggleBranchCollapsed, openReport, premiseViews, clearBranchPremise },
    calib: { forecastTracking, calibration, CALIBRATION_MODEL_ID },
    consts: {
      RES_SZ,
      STREAM_HALF,
      parseResolveDateLocal,
      resolutionMomentLocal,
      resolutionClampIso,
    },
    components: { ResolutionIcon, Tooltip },
    // Debug-only experiment: when enabled, GlowSpine replaces the dashed spine.
    glow: { enabled: glowSpine, controls: glowControls },
    misc: {
      forecasterDebugLabel,
      rootEngine,
      forecastEngine,
      forecastLiveProgressEnabled,
      debugResolutionOverride,
      resolvedScreenX,
      resolvedAtIso,
      earlyResolved,
      // Scrub-vs-verdict precedence (see scrubbedToForecast at the _resVerdictStatus
      // site): while a FORECAST vintage is scrubbed to, the lane's resolution marker
      // must show that moment's probability, not the resolution verdict. Scrubbing
      // to a resolution entry (or at rest) shows the verdict.
      scrubbedToForecast,
      // The PRIMARY lane's verdict, already resolved against the selected resolution
      // attempt (scrub/debug/entry-status). The primary lane uses this verbatim so
      // the marker matches App + the report modal; compare lanes derive their own
      // from their per-lane root.resolution (scrub is disabled in compare).
      primaryVerdictStatus: _resVerdictStatus,
      // Terminal-verdict flag INCLUDING a multi-value resolution, which settles by NAMING
      // an outcome and carries no yes/no/tie token at all (see `_resNamesOutcome`). The
      // lane must gate on this, not on the binary status: keying off the three
      // `resolved_*` strings alone left a resolved CATEGORICAL question outside the
      // resolved path entirely — it kept drawing pending triggers, and an emptied lane
      // still lost its spine. Null while scrubbed to a forecast vintage, same as the
      // status above, so a rewound pre-resolution view is unaffected.
      primaryVerdictTerminal: _resHasTerminal,
      // chatboard-all-multiway (from /api/me): ONE lane treatment regardless of question
      // type — a binary lane renders neutral triggers + the two-bar distribution instead
      // of the valenced triggers + probability pie.
      allMultiway: allMultiwayEnabled,
      resPulseDur: theme === "dev" || theme === "dusk" ? "2.67s" : "4s",
      detailFailed,
    },
  };
  return laneCtx;
}
